<template>
  <div>
    <div style="margin-bottom: 20px;">
      <Button
        :label="t('Adicionar Atributo de Item')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-item-attributes-add-btn"
        @click="handleNew"
      />
    </div>

    <!-- SOItemAttributes Table -->
    <s-o-item-attributes-table
      ref="soItemAttributesTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-item-attributes-table>

    <!-- SOItemAttributesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOItemAttributesTable from './SOItemAttributesTable.vue';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface Props {
  translations?: Record<string, unknown>;
}

const props = withDefaults(defineProps<Props>(), {
  translations: () => ({}),
});

const { t } = useI18n();

const soItemAttributesTableRef = ref<DataTableInstance | null>(null);

const loadData = async (): Promise<void> => {
  log('SOItemAttributesPage', 'loadData', 'Reloading data');
  if (soItemAttributesTableRef.value) {
    await soItemAttributesTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOItemAttributesPage', 'handleNew', 'Opening modal for new item attribute via eventBus');
  eventBus.emit('modal.so-item-attributes.open', { action: 'create' });
};

const handleEdit = (id: Id): void => {
  log('SOItemAttributesPage', 'handleEdit', 'Editing item attribute ID via eventBus', id);
  eventBus.emit('modal.so-item-attributes.open', { id, action: 'edit' });
};

const handleDelete = (id: Id, data: Record<string, unknown> | null = null): void => {
  log('SOItemAttributesPage', 'handleDelete', 'Deleting item attribute ID via eventBus', id);
  eventBus.emit('modal.so-item-attributes.open', { id, data, action: 'delete' });
};

const handleNewEvent = (): void => handleNew();
const handleEditEvent = (id?: Id): void => { if (id !== undefined) handleEdit(id); };
const handleDeleteEvent = (id?: Id): void => { if (id !== undefined) handleDelete(id); };

onMounted(() => {
  log('SOItemAttributesPage', 'mounted', 'Component mounted');

  eventBus.on('so-item-attribute.new', handleNewEvent);
  eventBus.on('so-item-attribute.edit', handleEditEvent);
  eventBus.on('so-item-attribute.delete', handleDeleteEvent);
});

onBeforeUnmount(() => {
  log('SOItemAttributesPage', 'beforeUnmount', 'Cleaning up event listeners');

  eventBus.off('so-item-attribute.new', handleNewEvent);
  eventBus.off('so-item-attribute.edit', handleEditEvent);
  eventBus.off('so-item-attribute.delete', handleDeleteEvent);
});

defineExpose({
  loadData,
  handleNew,
  handleEdit,
  handleDelete
});
</script>

<style scoped>
</style>
